import React, { useEffect, useState, useMemo } from "react";
import { FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import { getMatches } from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";
import {
  getAllBPointsGames,
  getBPointsGameDetails,
} from "../../services/bPointsGameResultService";

export const BPointsGamesResultListLayer = () => {
  const [games, setGames] = useState([]);
  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);

  const [leagueId, setLeagueId] = useState("");
  const [matchId, setMatchId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const [viewData, setViewData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    loadGames();
    loadMatches();
    loadLeagues();
  }, []);

  const loadGames = async () => {
    try {
      const res = await getAllBPointsGames();
      if (res?.status) setGames(res.data || []);
    } catch {
      toast.error("Failed to load games");
    }
  };

  const loadMatches = async () => {
    try {
      const res = await getMatches();
      if (res?.status) setMatches(res.data || []);
    } catch {
      toast.error("Failed to load matches");
    }
  };

  const loadLeagues = async () => {
    try {
      const res = await getLeagues();
      if (res?.status) setLeagues(res.data || []);
    } catch {
      toast.error("Failed to load leagues");
    }
  };

  /* ================= MATCH FILTER BY LEAGUE ================= */
  const filteredMatches = useMemo(() => {
    if (!leagueId) return matches;
    return matches.filter(
      (m) => String(m.league_id) === String(leagueId)
    );
  }, [leagueId, matches]);

  /* ================= FILTER GAMES ================= */
  const filtered = useMemo(() => {
    return games.filter((g) => {
      const leagueMatch = leagueId
        ? matches.find(
            (m) =>
              String(m.id) === String(g.match_id) &&
              String(m.league_id) === String(leagueId)
          )
        : true;

      const matchFilter = matchId
        ? String(g.match_id) === String(matchId)
        : true;

      const statusMatch = statusFilter
        ? g.status === statusFilter
        : true;

      const searchMatch = g.question
        ?.toLowerCase()
        .includes(search.toLowerCase());

      return leagueMatch && matchFilter && statusMatch && searchMatch;
    });
  }, [games, leagueId, matchId, statusFilter, search, matches]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filtered.length / pageSize);

  const paginated = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const handleView = async (id) => {
    try {
      const res = await getBPointsGameDetails(id);
      if (res?.status) setViewData(res.data);
    } catch {
      toast.error("Failed to load details");
    }
  };

  const getMatchName = (id) => {
    const match = matches.find(
      (m) => String(m.id) === String(id)
    );
    return match
      ? `${match.team_a} vs ${match.team_b}`
      : `Match ${id}`;
  };

  return (
    <div className="container-fluid mt-4">
      <div className="card shadow-sm border-0">

        <div className="card-header bg-white">
          <h5 className="fw-bold mb-0">Section B Results</h5>
        </div>

        <div className="card-body">

          {/* FILTER BAR */}
          <div className="row g-3 mb-3">

            <div className="col-12 col-md-2">
              <label className="form-label">League</label>
              <select
                className="form-select"
                value={leagueId}
                onChange={(e) => {
                  setLeagueId(e.target.value);
                  setMatchId("");
                  setPage(1);
                }}
              >
                <option value="">All</option>
                {leagues.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.league_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-2">
              <label className="form-label">Match</label>
              <select
                className="form-select"
                value={matchId}
                onChange={(e) => {
                  setMatchId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                {filteredMatches.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.team_a} vs {m.team_b}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="settled">Settled</option>
              </select>
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">Search</label>
              <input
                className="form-control"
                placeholder="Search question"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">Show</label>
              <select
                className="form-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

          </div>

          {/* TABLE */}
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Match</th>
                  <th>Question</th>
                  <th>Winner</th>
                  <th>Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length > 0 ? (
                  paginated.map((g, i) => (
                    <tr key={g.id}>
                      <td>{(page - 1) * pageSize + i + 1}</td>
                      <td>{getMatchName(g.match_id)}</td>
                      <td>{g.question}</td>
                      <td>
                        {g.win_answer
                          ? g.win_answer.toUpperCase()
                          : "-"}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            g.status === "settled"
                              ? "bg-success"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {g.status}
                        </span>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleView(g.id)}
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No results found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-3 flex-wrap">
              <ul className="pagination mb-0">
                {[...Array(totalPages)].map((_, i) => (
                  <li
                    key={i}
                    className={`page-item ${
                      page === i + 1 ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </div>

      {/* MODAL SAME */}
      {viewData && (
        <>
          <div className="modal fade show d-block" style={{ background: "#0008" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="fw-bold">{viewData.question}</h5>
                  <button
                    className="btn-close"
                    onClick={() => setViewData(null)}
                  />
                </div>
                <div className="modal-body">
                  <ul className="list-group mb-3">
                    <li className="list-group-item">
                      Total Entries: {viewData.report?.total_entries}
                    </li>
                    <li className="list-group-item">
                      Total Collection: ₹{viewData.report?.total_collection}
                    </li>
                    <li className="list-group-item">
                      Total Payout: ₹{viewData.report?.total_payout}
                    </li>
                    <li className="list-group-item">
                      Admin Profit: ₹{viewData.report?.admin_profit}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};